import { defineColumns } from './piColumns.js';
import { EMPTY, formatDate, formatDateFull, formatDays, formatDecimal, formatInteger, formatMonth, formatPercent, formatTime, isNumber, toInstant } from './piFormat.js';
import { journeyLabel, profileText } from './piQuery.js';
import { accountRoute, companyRoute } from './piNavigation.js';
import { listOf, objectOf } from './piRequests.js';
import { metricCell, numberOf, pctMetric, ratioText, shareOf } from './piTable.js';
import { sequentialStyle } from './piScale.js';
import { TRIAL_STATUS_LABEL, WINDOW_VALUES, originLabel, planLabel, sizeBandLabel, windowParam } from './piOnboardingFilters.js';

export const ONBOARDING_WINDOWS = WINDOW_VALUES;

export const DEFAULT_WINDOW = 30;

export const ONBOARDING_STAGES = [
  { stage: 0, label: 'Sem colaborador', short: 'Sem colaborador', text: 'Nenhum colaborador cadastrado até a janela.' },
  { stage: 1, label: 'Cadastrou colaboradores', short: 'Colaboradores', text: 'Tem colaborador, mas não configurou turno ou escala.' },
  { stage: 2, label: 'Configurou turno ou escala', short: 'Turno', text: 'Configurou turno ou escala, mas ninguém bateu ponto.' },
  { stage: 3, label: 'Bateu ponto', short: 'Bateu ponto', text: 'Já houve batida de colaborador, mas o ponto ainda não entrou em operação.' },
  { stage: 4, label: 'Em operação', short: 'Em operação', text: 'Primeiro valor: numa semana, batidas em 3 ou mais dias e de pelo menos metade dos cadastrados.' },
  { stage: 5, label: 'Rotina', short: 'Rotina', text: 'Na semana que fecha a janela, batidas em 4 ou mais dias e de pelo menos metade dos cadastrados.' },
];

export const STAGE_COLORS = ['#e5e7eb', '#dbe6fd', '#b9cefb', '#8fb0f9', '#2f66f3', '#1a45b8'];

export const MILESTONE_GROUPS = [
  { key: 'setup', label: 'Setup' },
  { key: 'value', label: 'Valor' },
  { key: 'habit', label: 'Hábito' },
];

export const ONBOARDING_MILESTONES = [
  { key: 'employee', group: 'setup', label: 'Cadastrou colaboradores' },
  { key: 'manager', group: 'setup', label: 'Gestor ou admin além do dono' },
  { key: 'workshift', group: 'setup', label: 'Turno ou escala configurado' },
  { key: 'team', group: 'setup', label: 'Equipe própria' },
  { key: 'geofence', group: 'setup', label: 'Cerco próprio' },
  { key: 'device', group: 'setup', label: 'MarQTab ou REP' },
  { key: 'punch', group: 'value', label: '1ª batida (ativada)' },
  { key: 'value', group: 'value', label: 'Primeiro valor: ponto em operação' },
  { key: 'mirror', group: 'value', label: '1º espelho ou fechamento' },
  { key: 'habit', group: 'habit', label: 'Rotina na semana do D+N' },
];

export const RULES_NOTE = 'Turno ou escala configurado = turno criado pelo cliente, turno padrão editado depois do 1º minuto ou escala publicada; '
  + 'as regras de ponto da empresa (tolerância, horas extras, banco de horas, DSR, noturno, fechamento) não são medidas.';

export const DEFAULTS_NOTE = 'A equipe e o cerco que nascem com a empresa (nos primeiros 60 segundos) não contam, mesmo renomeados, '
  + 'e o cerco chamado "Cerco da empresa" não conta nem quando é recriado depois.';

export const NOT_FOUND_NOTE = 'Registro não encontrado nos agregados.';

export const CENSORED_NOTE_PREFIX = 'Fora dos denominadores:';

export const ONBOARDING_CARDS = [
  { key: 'started', label: 'Iniciaram o uso', definitionKey: 'onboarding_started', infoKey: 'activation.cardStarted', hint: 'Cadastraram colaboradores até a janela.' },
  { key: 'punch', label: 'Bateram ponto', definitionKey: 'onboarding_milestone_rate', infoKey: 'activation.cardPunch', hint: '1ª batida de colaborador até a janela.', median: 'até a 1ª batida' },
  { key: 'value', label: 'Primeiro valor', definitionKey: 'first_value_rate', infoKey: 'activation.cardValue', hint: 'Ponto em operação: numa semana, 3 ou mais dias com batida e pelo menos metade dos cadastrados.', median: 'até o valor' },
  { key: 'habit', label: 'Rotina', definitionKey: 'habit_rate', infoKey: 'activation.cardHabit', hint: 'Na semana que fecha a janela, 4 ou mais dias com batida e pelo menos metade dos cadastrados.' },
];

export const ACTIVATION_RATE_INFO_KEY = 'activation.cardPunch30';

export const COHORT_INDICATORS = [
  { key: 'started', label: 'Iniciou' },
  { key: 'punch', label: 'Bateu ponto' },
  { key: 'value', label: 'Primeiro valor' },
  { key: 'habit', label: 'Rotina' },
  { key: 'mirror', label: 'Espelho' },
];

export const DEFAULT_INDICATOR = 'punch';

export const SMALL_SAMPLE = 20;

export const TTV_BANDS = [
  { band: '0-3', label: '0 a 3 dias' },
  { band: '4-7', label: '4 a 7 dias' },
  { band: '8-14', label: '8 a 14 dias' },
  { band: '15-30', label: '15 a 30 dias' },
  { band: '31-60', label: '31 a 60 dias' },
  { band: 'none', label: 'Não atingiu até D+60' },
];

export const STUCK_REASONS = [
  { key: 'no_employee', label: 'Sem colaboradores' },
  { key: 'no_workshift', label: 'Sem turno ou escala' },
  { key: 'no_punch', label: 'Sem batida' },
  { key: 'no_value', label: 'Ponto não decolou' },
  { key: 'stopped', label: 'Parou de bater' },
  { key: 'incomplete', label: 'Cadastro incompleto' },
];

export const SOLD_SOURCES = {
  R: { label: 'Venda registrada no Master', approximate: false },
  F: { label: 'Liberação de funcionalidade da venda', approximate: false },
  P: { label: 'Data do 1º pagamento do contrato', approximate: true },
  C: { label: 'Criação da empresa (paga, sem venda registrada)', approximate: true },
  N: { label: 'Não vendida (D0 = criação)', approximate: false },
};

const STATUS_NOW = {
  active: { label: 'Ativa', tone: 'ok' },
  churned: { label: 'Cancelada ou excluída', tone: 'muted' },
  unpaid: { label: 'Vendida sem pagar (ainda trial)', tone: 'warn' },
};

const TRIAL_NOW = { label: TRIAL_STATUS_LABEL, tone: 'muted' };

export const SORTABLE = {
  companies: ['name', 'soldAt', 'daysSinceSale', 'employees', 'overdueDays', 'stage'],
  stuck: ['overdueDays', 'daysSinceSale', 'name', 'lastProgress'],
};

export const DEFAULT_SORT = {
  companies: { sort: 'soldAt', desc: true },
  stuck: { sort: 'overdueDays', desc: true },
};

export const LIST_PAGE_SIZE = 25;

export const QUANTITY_GROUP = 'Quantidades';

const textOf = (value) => (value === null || value === undefined ? '' : String(value));

const plainKey = (value) => textOf(value).toLowerCase()
  .replace(/[áàâã]/g, 'a').replace(/[éê]/g, 'e').replace(/í/g, 'i').replace(/[óôõ]/g, 'o').replace(/ú/g, 'u').replace(/ç/g, 'c')
  .replace(/[^a-z0-9>+]/g, '');

const isObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

const metricOf = (value) => (isObject(value) ? value : null);

const plural = (count, one, many) => formatInteger(count) + ' ' + (count === 1 ? one : many);

const companiesText = (count) => (isNumber(count) ? plural(count, 'empresa', 'empresas') : EMPTY);

export const windowLabel = (window) => 'D' + window;

export const windowUntil = (window) => 'D+' + window;

export const validWindow = (value) => {
  const window = windowParam(value);
  return window === null ? DEFAULT_WINDOW : window;
};

const byWindow = (list, window) => listOf(list).filter((item) => windowParam(item.window) === window)[0] || null;

export const countsByWindow = (value) => {
  const counts = {};
  ONBOARDING_WINDOWS.forEach((window) => {
    counts[window] = null;
  });
  listOf(value).forEach((item) => {
    const window = item ? windowParam(item.window) : null;
    const count = item ? numberOf(item.companies) : null;
    if (window !== null && isNumber(count)) counts[window] = count;
  });
  return counts;
};

export const envelopeWithoutNotes = (envelope, hidden) => {
  if (!isObject(envelope)) return envelope;
  const skip = listOf(hidden).concat([NOT_FOUND_NOTE]);
  const notes = envelope.data === null ? [] : listOf(envelope.notes).filter((note) => skip.indexOf(note) === -1);
  return Object.assign({}, envelope, { notes: notes });
};

export const summaryHiddenNotes = (envelope) => [RULES_NOTE].concat(listOf(objectOf(envelope).notes)
  .filter((note) => textOf(note).indexOf(CENSORED_NOTE_PREFIX) === 0));

export const sharedNotes = (envelope) => [RULES_NOTE].concat(listOf(objectOf(envelope).notes));

export const stageInfo = (stage) => {
  const number = typeof stage === 'string' && /^\d$/.test(stage.trim()) ? Number(stage) : stage;
  return ONBOARDING_STAGES.filter((item) => item.stage === number)[0] || null;
};

export const stageLabel = (stage) => {
  const info = stageInfo(stage);
  return info ? info.label : (textOf(stage) || EMPTY);
};

export const milestoneInfo = (key) => {
  const wanted = plainKey(key);
  return ONBOARDING_MILESTONES.filter((item) => plainKey(item.key) === wanted)[0] || null;
};

export const milestoneLabel = (key) => {
  const info = milestoneInfo(key);
  return info ? info.label : (textOf(key) || EMPTY);
};

const GROUP_ALIASES = { setup: 'setup', configuracao: 'setup', value: 'value', valor: 'value', habit: 'habit', habito: 'habit', rotina: 'habit' };

export const groupKey = (group, key) => {
  const alias = GROUP_ALIASES[plainKey(group)];
  if (alias) return alias;
  const info = milestoneInfo(key);
  return info ? info.group : 'setup';
};

export const reasonInfo = (reason) => {
  const wanted = textOf(reason).trim();
  return STUCK_REASONS.filter((item) => item.key === wanted)[0] || null;
};

export const reasonLabel = (reason, label) => {
  const info = reasonInfo(reason);
  return textOf(label).trim() || (info ? info.label : (textOf(reason) || EMPTY));
};

const reasonOrder = (reason) => {
  const info = reasonInfo(reason);
  return info ? STUCK_REASONS.indexOf(info) : STUCK_REASONS.length;
};

export const statusNowInfo = (status, isSold) => {
  const raw = textOf(status).trim();
  if (!raw) return { label: EMPTY, tone: 'muted' };
  const key = raw.toLowerCase();
  if (key === 'unpaid' && isSold === false) return TRIAL_NOW;
  return STATUS_NOW[key] || { label: raw, tone: 'muted' };
};

export const soldSourceInfo = (source) => {
  const key = textOf(source).trim().toUpperCase();
  const info = SOLD_SOURCES[key];
  return info ? Object.assign({ key: key }, info) : { key: key, label: key || EMPTY, approximate: false };
};

const dayText = (day) => (isNumber(day) ? 'D+' + formatInteger(day) : '');

const ratioValueText = (value) => (isNumber(value) ? formatDecimal(value, 2) : EMPTY);

export const updatedText = (value) => {
  const instant = toInstant(value);
  return instant ? 'Atualizado em ' + formatDate(instant) + ' ' + formatTime(instant) : '';
};

export const cohortRangeText = (from, to) => {
  const start = formatDateFull(from);
  const end = formatDateFull(to);
  if (start === EMPTY || end === EMPTY) return '';
  return 'Vendas de ' + start + ' a ' + end;
};

const medianDaysOf = (value) => {
  const view = metricCell(metricOf(value), {});
  return view.visible ? view.metric.value : null;
};

export const onboardingCards = (data, window) => {
  const source = objectOf(data);
  const wanted = validWindow(window);
  const cards = listOf(source.cards).filter((card) => windowParam(card.window) === wanted);
  return ONBOARDING_CARDS.map((definition) => {
    const card = cards.filter((item) => textOf(item.key) === definition.key)[0] || {};
    const metric = metricOf(card.metric);
    const notMeasured = numberOf(card.notMeasured);
    const median = definition.median ? medianDaysOf(card.medianDays) : null;
    const notes = [];
    if (definition.key === 'punch' && wanted === 30) notes.push('Activation Rate v2');
    if (isNumber(median)) notes.push('mediana de ' + formatDays(median) + ' ' + definition.median);
    if (isNumber(notMeasured) && notMeasured > 0) notes.push('não medido: ' + companiesText(notMeasured));
    const activationRate = definition.key === 'punch' && wanted === 30;
    return {
      key: definition.key,
      label: textOf(card.label) || definition.label,
      definitionKey: (metric && textOf(metric.definitionKey)) || (activationRate ? 'activation_rate_30d' : definition.definitionKey),
      infoKey: activationRate ? ACTIVATION_RATE_INFO_KEY : definition.infoKey,
      metric: metric,
      eligible: numberOf(card.eligible),
      open: numberOf(card.open),
      awaiting: numberOf(card.awaiting),
      hint: definition.hint,
      notes: notes,
    };
  });
};

const matrixCell = (cell, window, row, compare) => {
  const label = row.label;
  if (!cell) {
    return {
      key: row.key + '-' + window, window: window, text: EMPTY, ratio: '', badge: '', notMeasuredText: '', style: {},
      clickable: false, title: 'Sem dado até ' + windowUntil(window), ariaLabel: '', rate: null, reached: null, eligible: null, base: null, notMeasured: null,
    };
  }
  const reached = numberOf(cell.reached);
  const eligible = numberOf(cell.eligible);
  const notMeasured = numberOf(cell.notMeasured);
  const raw = metricOf(cell.rate) || pctMetric(reached, eligible);
  const view = metricCell(raw, { compare: compare });
  const color = sequentialStyle(view.visible ? view.metric.value : null);
  const ratio = ratioText(reached, eligible);
  const notMeasuredText = isNumber(notMeasured) && notMeasured > 0 ? plural(notMeasured, 'não medida', 'não medidas') : '';
  return {
    key: row.key + '-' + window,
    window: window,
    text: view.text,
    ratio: ratio,
    badge: view.metric.state === 'partial' ? '' : view.badge,
    delta: view.delta,
    notMeasuredText: notMeasuredText,
    style: view.visible ? { background: color.background, color: color.color } : {},
    clickable: isNumber(eligible) && eligible > 0,
    rate: view.metric,
    reached: reached,
    eligible: eligible,
    base: eligible,
    notMeasured: notMeasured,
    title: label + ' até ' + windowUntil(window) + ': ' + view.text + (ratio ? ' (' + ratio + ')' : '') + (notMeasuredText ? ' · ' + notMeasuredText : '')
      + (view.reason ? ' · ' + view.reason : ''),
    ariaLabel: label + ' até ' + windowUntil(window) + ': ' + view.text + (ratio ? ', ' + ratio : '') + '. Ver as que não chegaram.',
  };
};

const bottleneckText = (value) => (value === true ? 'gargalo' : '');

export const matrixRows = (data, compare) => {
  const list = listOf(objectOf(data).milestones);
  const ordered = [];
  ONBOARDING_MILESTONES.forEach((definition) => {
    const found = list.filter((row) => plainKey(row.key) === plainKey(definition.key))[0];
    if (found) ordered.push({ definition: definition, row: found });
  });
  list.forEach((row) => {
    if (!milestoneInfo(row.key)) ordered.push({ definition: null, row: row });
  });
  return ordered.map((item) => {
    const key = item.definition ? item.definition.key : textOf(item.row.key);
    const base = {
      key: key,
      label: textOf(item.row.label) || (item.definition ? item.definition.label : key),
      group: groupKey(item.row.group, key),
      bottleneck: bottleneckText(item.row.bottleneck),
    };
    base.cells = ONBOARDING_WINDOWS.map((window) => matrixCell(byWindow(item.row.cells, window), window, base, compare));
    return base;
  });
};

export const matrixGroups = (data, compare) => {
  const rows = matrixRows(data, compare);
  return MILESTONE_GROUPS
    .map((group) => ({ key: group.key, label: group.label, rows: rows.filter((row) => row.group === group.key) }))
    .filter((group) => group.rows.length);
};

const NO_RATE = { value: null, unit: 'pct', state: 'ok' };

const QUANTITY_LABELS = {
  employees: 'Colaboradores cadastrados',
  weekPunchDays: 'Dias com batida na semana do D+N',
  weekPunchShare: 'Bateram ÷ cadastrados na semana do D+N',
};

const quantityText = (median, p75, unit) => {
  if (!isNumber(median)) return EMPTY;
  const format = (value) => (unit === 'ratio' ? ratioValueText(value) : formatDecimal(value, Math.round(value) === value ? 0 : 1));
  return format(median) + (isNumber(p75) ? ' · p75 ' + format(p75) : '');
};

export const quantityRows = (data) => {
  const source = objectOf(data);
  const list = listOf(source.quantities).filter((item) => textOf(item.key));
  const keys = [];
  list.forEach((item) => {
    if (keys.indexOf(textOf(item.key)) === -1) keys.push(textOf(item.key));
  });
  const rows = keys.map((key) => {
    const items = list.filter((item) => textOf(item.key) === key);
    const label = textOf(items[0].label) || QUANTITY_LABELS[key] || key;
    return {
      key: key,
      label: label + ' (mediana · p75)',
      cells: ONBOARDING_WINDOWS.map((window) => {
        const found = items.filter((item) => windowParam(item.window) === window)[0];
        const companies = found ? numberOf(found.companies) : null;
        return {
          key: key + '-' + window,
          window: window,
          text: found ? quantityText(numberOf(found.median), numberOf(found.p75), textOf(found.unit)) : EMPTY,
          title: isNumber(companies) ? windowLabel(window) + ': ' + companiesText(companies) : '',
          rate: NO_RATE,
          base: companies,
          median: found ? numberOf(found.median) : null,
          p75: found ? numberOf(found.p75) : null,
        };
      }),
    };
  });
  const seats = listOf(source.seats);
  if (seats.length) {
    rows.push({
      key: 'seatsRatio',
      label: 'Cadastrados ÷ assentos contratados (mediana)',
      cells: ONBOARDING_WINDOWS.map((window) => {
        const found = byWindow(seats, window);
        const companies = found ? numberOf(found.companies) : null;
        return {
          key: 'seatsRatio-' + window,
          window: window,
          text: found ? ratioValueText(numberOf(found.medianRatio)) : EMPTY,
          title: isNumber(companies) ? windowLabel(window) + ': ' + companiesText(companies) + ' com assentos contratados' : '',
          rate: NO_RATE,
          base: companies,
          median: found ? numberOf(found.medianRatio) : null,
          p75: null,
        };
      }),
    });
    rows.push({
      key: 'seats80',
      label: 'Com 80% ou mais dos assentos cadastrados',
      cells: ONBOARDING_WINDOWS.map((window) => {
        const found = byWindow(seats, window);
        const view = metricCell(found ? metricOf(found.shareAtLeast80) : null, {});
        return {
          key: 'seats80-' + window,
          window: window,
          text: found && view.visible ? view.text : EMPTY,
          title: found ? (view.ratio || view.reason) : '',
          rate: found ? view.metric : NO_RATE,
          base: found ? numberOf(found.companies) : null,
          median: null,
          p75: null,
        };
      }),
    });
  }
  return rows;
};

const windowsText = (counts) => ONBOARDING_WINDOWS
  .filter((window) => isNumber(counts[window]) && counts[window] > 0)
  .map((window) => windowLabel(window) + ' ' + formatInteger(counts[window]))
  .join(' · ');

export const censusNotes = (coverage) => {
  const source = objectOf(coverage);
  const notes = [];
  const companies = numberOf(source.companies);
  const sold = numberOf(source.sold);
  if (isNumber(companies)) {
    notes.push('Empresas no recorte: ' + formatInteger(companies) + (isNumber(sold) && sold !== companies ? ' (' + formatInteger(sold) + ' vendidas)' : '')
      + '. Cada coluna conta só as empresas com a janela fechada; o n de cada célula é o dela.');
  }
  const open = windowsText(countsByWindow(source.openByWindow));
  if (open) notes.push('Em andamento (janela ainda aberta, fora do denominador): ' + open + '.');
  const awaiting = windowsText(countsByWindow(source.awaitingByWindow));
  if (awaiting) notes.push('Aguardando medição (janela fechada, ainda não gravada pelo job da noite): ' + awaiting + '.');
  const purged = numberOf(source.purged);
  if (isNumber(purged) && purged > 0) notes.push('Expurgadas (excluídas sem nenhum registro da criação), fora dos denominadores: ' + formatInteger(purged) + '.');
  const missing = notMeasuredItems(source.notMeasuredByMilestone);
  if (missing.length) {
    notes.push('Não medido (índice ausente no banco da empresa), fora do denominador daquele marco: '
      + missing.map((item) => item.label + ' ' + windowLabel(item.window) + ' ' + formatInteger(item.companies)).join(' · ') + '.');
  }
  const share = numberOf(source.sourceBShare);
  if (isNumber(share) && share > 0) notes.push('Gravadas retroativamente (depois do fechamento): ' + formatPercent(share) + ' das janelas.');
  return notes;
};

const milestoneOrder = (key) => {
  const info = milestoneInfo(key);
  return info ? ONBOARDING_MILESTONES.indexOf(info) : ONBOARDING_MILESTONES.length;
};

export const notMeasuredItems = (value) => listOf(value)
  .filter((item) => item && textOf(item.key) && windowParam(item.window) !== null && isNumber(numberOf(item.companies)) && numberOf(item.companies) > 0)
  .map((item) => ({ key: textOf(item.key), label: milestoneLabel(item.key), window: windowParam(item.window), companies: numberOf(item.companies) }))
  .sort((a, b) => (milestoneOrder(a.key) - milestoneOrder(b.key)) || (a.window - b.window));

export const stageBarRows = (data) => {
  const list = listOf(objectOf(data).stageBars);
  return ONBOARDING_WINDOWS.map((window) => {
    const bar = byWindow(list, window);
    const eligible = bar ? numberOf(bar.eligible) : null;
    const notMeasured = bar ? numberOf(bar.notMeasured) : null;
    const segments = ONBOARDING_STAGES.map((info) => {
      const found = bar ? listOf(bar.segments).filter((segment) => stageInfo(segment.stage) === info)[0] : null;
      const companies = found ? numberOf(found.companies) : null;
      const computed = shareOf(companies, eligible);
      const share = computed !== null ? computed : (found ? numberOf(found.share) : null);
      const shareText = isNumber(share) ? formatPercent(share) : EMPTY;
      const label = (found && textOf(found.label)) || info.label;
      return {
        key: window + '-' + info.stage,
        stage: info.stage,
        window: window,
        label: label,
        short: info.short,
        companies: companies,
        share: share,
        shareText: shareText,
        widthPct: isNumber(share) ? Math.max(0, Math.min(100, share)) : 0,
        showText: isNumber(share) && share >= 8,
        background: STAGE_COLORS[info.stage],
        color: info.stage >= 4 ? '#ffffff' : '#1f2933',
        title: windowLabel(window) + ' · ' + label + ': ' + companiesText(companies) + ' (' + shareText + ')',
        ariaLabel: 'Até ' + windowUntil(window) + ', pararam em ' + label + ': ' + companiesText(companies) + ' (' + shareText + '). Ver a lista.',
      };
    });
    return {
      key: String(window),
      window: window,
      label: windowLabel(window),
      eligible: eligible,
      notMeasured: notMeasured,
      eligibleText: companiesText(eligible) + (isNumber(notMeasured) && notMeasured > 0 ? ' · ' + plural(notMeasured, 'não medida', 'não medidas') : ''),
      empty: !isNumber(eligible) || eligible <= 0,
      segments: segments,
    };
  });
};

export const bottleneckRows = (data, compare) => listOf(objectOf(data).bottlenecks).map((item, index) => {
  const window = windowParam(item.window);
  const conversion = metricCell(metricOf(item.conversion) || pctMetric(null, null), { compare: compare });
  const lost = numberOf(item.lost);
  const base = numberOf(item.base);
  const from = stageLabel(item.fromStage);
  const to = stageLabel(item.toStage);
  const lostShare = conversion.visible ? Math.max(0, 100 - conversion.metric.value) : shareOf(lost, base);
  const fallback = 'Até ' + (window ? windowLabel(window) : 'a janela') + ', ' + (isNumber(lostShare) ? formatPercent(lostShare) : EMPTY)
    + ' das que chegaram a "' + from + '" não chegaram a "' + to + '"' + (isNumber(lost) && isNumber(base) ? ' (' + ratioText(lost, base) + ')' : '') + '.';
  return {
    key: textOf(item.fromStage) + '-' + textOf(item.toStage) + '-' + (window || index),
    window: window,
    windowText: window ? windowLabel(window) : '',
    fromText: from,
    toText: to,
    conversion: conversion,
    lost: lost,
    base: base,
    text: textOf(item.text).trim() || fallback,
    baseText: isNumber(base) ? 'base ' + companiesText(base) : '',
  };
});

export const timeToMilestoneRows = (data) => listOf(objectOf(data).timeToMilestone)
  .slice()
  .sort((a, b) => milestoneOrder(a.key) - milestoneOrder(b.key))
  .map((item) => {
    const reached = numberOf(item.reached);
    const eligible = numberOf(item.eligible);
    return {
      key: textOf(item.key),
      label: textOf(item.label) || milestoneLabel(item.key),
      reached: reached,
      eligible: eligible,
      medianDays: numberOf(item.medianDays),
      p75Days: numberOf(item.p75Days),
      reachedText: isNumber(reached) && isNumber(eligible) ? ratioText(reached, eligible) : companiesText(reached),
      medianText: formatDays(numberOf(item.medianDays)),
      p75Text: formatDays(numberOf(item.p75Days)),
    };
  });

const ttvBand = (band) => TTV_BANDS.filter((item) => item.band === textOf(band).trim())[0] || null;

export const ttvHistogramRows = (data) => {
  const list = listOf(objectOf(data).ttvHistogram);
  const rows = TTV_BANDS.map((band) => {
    const found = list.filter((item) => ttvBand(item.band) === band)[0];
    return { key: band.band, label: (found && textOf(found.label)) || band.label, companies: found ? numberOf(found.companies) : null };
  });
  list.forEach((item) => {
    if (!ttvBand(item.band)) rows.push({ key: textOf(item.band), label: textOf(item.label) || textOf(item.band) || 'Sem faixa', companies: numberOf(item.companies) });
  });
  const total = rows.reduce((sum, row) => sum + (isNumber(row.companies) ? row.companies : 0), 0);
  return rows.map((row) => {
    const share = shareOf(row.companies, total);
    return {
      key: row.key,
      label: row.label,
      value: row.companies,
      share: share,
      valueText: isNumber(row.companies) ? companiesText(row.companies) + (share === null ? '' : ' (' + formatPercent(share) + ')') : EMPTY,
      title: row.label,
    };
  });
};

const QUARTER = /^(\d{4})-?[QqTt]([1-4])$/;
const MONTH = /^(\d{4})-(\d{2})/;

export const cohortLabel = (cohort, granularity) => {
  const raw = textOf(cohort).trim();
  const quarter = QUARTER.exec(raw);
  if (quarter) return quarter[2] + 'º tri/' + quarter[1];
  const month = MONTH.exec(raw);
  if (!month) return raw || EMPTY;
  if (granularity === 'quarter') return (Math.floor((Number(month[2]) - 1) / 3) + 1) + 'º tri/' + month[1];
  return formatMonth(raw);
};

export const cohortKey = (cohort) => {
  const raw = textOf(cohort).trim();
  const quarter = QUARTER.exec(raw);
  if (quarter) return quarter[1] + '-Q' + quarter[2];
  const month = MONTH.exec(raw);
  return month ? month[1] + '-' + month[2] : raw;
};

export const validIndicator = (value) => (COHORT_INDICATORS.some((item) => item.key === value) ? value : DEFAULT_INDICATOR);

const indicatorMetric = (windowRow, indicator) => {
  const indicators = objectOf(windowRow && windowRow.indicators);
  return metricOf(indicators[indicator]);
};

const sortedCohorts = (rows) => listOf(rows)
  .filter((row) => textOf(row.cohort).trim())
  .slice()
  .sort((a, b) => (cohortKey(a.cohort) < cohortKey(b.cohort) ? -1 : (cohortKey(a.cohort) > cohortKey(b.cohort) ? 1 : 0)));

const measuredCompanies = (row) => {
  const companies = numberOf(row.companies);
  const purged = numberOf(row.purged);
  if (!isNumber(companies)) return numberOf(row.sold);
  return isNumber(purged) ? Math.max(0, companies - purged) : companies;
};

const positive = (value) => isNumber(value) && value > 0;

const smallSampleOf = (row) => {
  const measured = measuredCompanies(row);
  return typeof row.smallSample === 'boolean' ? row.smallSample : (isNumber(measured) && measured < SMALL_SAMPLE);
};

const cohortCell = (row, window, indicator, compare) => {
  const found = byWindow(row.windows, window);
  if (!found) return { key: window, window: window, text: EMPTY, sub: '', open: false, title: '' };
  const base = measuredCompanies(row);
  const eligible = numberOf(found.eligible);
  const open = numberOf(found.open);
  const awaiting = numberOf(found.awaiting);
  const pending = positive(open) || positive(awaiting);
  const cell = metricCell(indicatorMetric(found, indicator), { compare: compare, ratio: false });
  if (!positive(eligible)) {
    if (positive(open)) {
      return {
        key: window, window: window, text: 'em andamento', sub: '(' + ratioText(open, isNumber(base) ? base : open) + ')', open: true,
        title: 'Janela ' + windowLabel(window) + ' ainda aberta para ' + companiesText(open) + '.',
      };
    }
    if (positive(awaiting)) {
      return {
        key: window, window: window, text: 'aguardando medição', sub: '(' + ratioText(awaiting, isNumber(base) ? base : awaiting) + ')', open: true,
        title: 'Janela ' + windowLabel(window) + ' fechada, ainda não gravada para ' + companiesText(awaiting) + '.',
      };
    }
    return { key: window, window: window, text: EMPTY, sub: '', open: false, title: cell.reason };
  }
  const numerator = cell.metric.numerator;
  const denominator = cell.metric.denominator;
  const ratio = isNumber(numerator) && isNumber(denominator) ? ratioText(numerator, denominator) : '';
  const parts = [];
  if (ratio) parts.push('(' + ratio + ')');
  if (positive(open)) parts.push(formatInteger(open) + ' em andamento');
  if (positive(awaiting)) parts.push(formatInteger(awaiting) + ' aguardando');
  return {
    key: window,
    window: window,
    text: cell.text,
    badge: cell.metric.state === 'partial' ? '' : cell.badge,
    sub: parts.join(' · '),
    open: pending,
    title: windowLabel(window) + ': ' + cell.text + (ratio ? ', ' + ratio + ' com a janela fechada' : '')
      + (positive(open) ? '; ' + companiesText(open) + ' em andamento' : '')
      + (positive(awaiting) ? '; ' + companiesText(awaiting) + ' aguardando medição' : ''),
  };
};

export const cohortRows = (rows, indicator, granularity, compare) => {
  const wanted = validIndicator(indicator);
  return sortedCohorts(rows).map((row) => {
    const companies = numberOf(row.companies);
    const sold = numberOf(row.sold);
    const purged = numberOf(row.purged);
    const count = isNumber(companies) ? companies : sold;
    const share = numberOf(row.sourceBShare);
    const median = metricCell(metricOf(row.medianDaysToPunch), {});
    return {
      key: cohortKey(row.cohort),
      label: cohortLabel(row.cohort, granularity),
      countText: isNumber(count) ? formatInteger(count) : EMPTY,
      countSub: [
        isNumber(sold) && isNumber(companies) && sold !== companies ? formatInteger(sold) + ' vendidas' : '',
        positive(purged) ? plural(purged, 'expurgada', 'expurgadas') : '',
      ].filter(Boolean).join(' · '),
      smallSample: smallSampleOf(row),
      sourceBText: isNumber(share) ? formatPercent(share) : EMPTY,
      medianPunchText: median.visible ? formatDays(median.metric.value) : EMPTY,
      medianPunchTitle: median.visible ? '' : median.reason,
      medianEmployeesText: isNumber(numberOf(row.medianEmployees30)) ? formatDecimal(numberOf(row.medianEmployees30), 1) : EMPTY,
      cells: ONBOARDING_WINDOWS.map((window) => cohortCell(row, window, wanted, compare)),
    };
  });
};

export const cohortSeries = (rows, indicator) => {
  const wanted = validIndicator(indicator);
  const sorted = sortedCohorts(rows);
  return ONBOARDING_WINDOWS.map((window) => ({
    key: 'd' + window,
    label: windowLabel(window),
    points: sorted.map((row) => {
      const found = byWindow(row.windows, window);
      const eligible = found ? numberOf(found.eligible) : null;
      const cell = metricCell(found && positive(eligible) ? indicatorMetric(found, wanted) : null, {});
      const pending = !!found && (positive(numberOf(found.open)) || positive(numberOf(found.awaiting)));
      return { date: cohortKey(row.cohort), value: cell.visible ? cell.metric.value : null, partial: pending };
    }),
  }));
};

export const cohortLabelFormatter = (granularity) => (key) => cohortLabel(key, granularity);

export const milestoneItems = (milestones, skippedSetup) => {
  const map = {};
  if (Array.isArray(milestones)) {
    milestones.forEach((item) => {
      if (item && item.key) map[plainKey(item.key)] = item;
    });
  } else {
    Object.keys(objectOf(milestones)).forEach((key) => {
      map[plainKey(key)] = Object.assign({ key: key }, objectOf(milestones[key]));
    });
  }
  const known = ONBOARDING_MILESTONES.map((item) => plainKey(item.key));
  const extra = Object.keys(map).filter((key) => known.indexOf(key) === -1);
  return ONBOARDING_MILESTONES.filter((item) => !!map[plainKey(item.key)]).map((item) => ({ definition: item, raw: map[plainKey(item.key)] }))
    .concat(extra.map((key) => ({ definition: null, raw: map[key] })))
    .map((entry) => {
      const raw = entry.raw;
      const key = entry.definition ? entry.definition.key : textOf(raw.key);
      const on = textOf(raw.on).trim();
      const measured = raw.measured !== false;
      const reached = !!on && formatDateFull(on) !== EMPTY;
      const notes = [];
      if (raw.beforeSale === true) notes.push('antes da venda');
      if (!measured) notes.push('não medido');
      if (key === 'workshift' && skippedSetup === true && !reached) notes.push('pulou a configuração');
      return {
        key: key,
        label: textOf(raw.label) || (entry.definition ? entry.definition.label : key),
        group: groupKey(raw.group, key),
        reached: reached,
        measured: measured,
        dateText: reached ? formatDateFull(on) : '',
        dayText: reached ? dayText(numberOf(raw.day)) : '',
        notes: notes,
        state: !measured ? 'unmeasured' : (reached ? 'done' : 'pending'),
      };
    });
};

export const milestoneGroups = (milestones, skippedSetup) => {
  const items = milestoneItems(milestones, skippedSetup);
  return MILESTONE_GROUPS
    .map((group) => ({ key: group.key, label: group.label, items: items.filter((item) => item.group === group.key) }))
    .filter((group) => group.items.length);
};

const dateOrEmpty = (value) => {
  const text = formatDateFull(value);
  return text === EMPTY ? '' : text;
};

const recordedText = (windowRow) => {
  if (!windowRow) return '';
  if (windowRow.closed !== true) return 'em andamento';
  return windowRow.recorded === true ? '' : 'aguardando medição';
};

export const windowValuesText = (windowRow) => {
  const source = objectOf(windowRow);
  const pending = recordedText(source);
  if (pending) return pending;
  const employees = numberOf(source.employees);
  const punchDays = numberOf(source.weekPunchDays);
  const users = numberOf(source.weekPunchUsers);
  const parts = [];
  if (isNumber(employees)) parts.push(plural(employees, 'colab.', 'colab.'));
  if (isNumber(punchDays)) parts.push(plural(punchDays, 'dia', 'dias') + ' com batida');
  if (isNumber(users) && isNumber(employees) && employees > 0) parts.push(ratioText(users, employees) + ' bateram');
  return parts.join(' · ');
};

export const onboardingCompanyRow = (row, index) => {
  const source = objectOf(row);
  const sold = soldSourceInfo(source.soldSource);
  const stuck = isObject(source.stuck) ? source.stuck : null;
  const registered = numberOf(source.employeesRegistered);
  const enabled = numberOf(source.employeesNow);
  const seats = numberOf(source.seatsContracted);
  const seatsRatio = numberOf(source.seatsRatio);
  const firstPunch = dateOrEmpty(source.firstPunchOn);
  const lastPunch = dateOrEmpty(source.lastPunchOn);
  const days7 = numberOf(source.punchDays7d);
  const users7 = numberOf(source.punchUsers7d);
  const overdue = stuck ? numberOf(stuck.overdueDays) : null;
  const daysSinceSale = numberOf(source.daysSinceSale);
  const windowRow = isObject(source.window) ? source.window : null;
  const windowStage = windowRow && !recordedText(windowRow) ? stageInfo(windowRow.stage) : null;
  const profile = [planLabel(source.planCode), sizeBandLabel(source.sizeBand), journeyLabel(source.journey)].filter(Boolean);
  const segment = profileText(textOf(source.segment));
  if (segment) profile.push(segment);
  const d0 = dateOrEmpty(source.d0On) || dateOrEmpty(source.soldAt);
  return {
    key: textOf(source.shardCode) + ':' + (textOf(source.companyId) || String(index || 0)),
    companyId: textOf(source.companyId),
    shardCode: textOf(source.shardCode),
    name: textOf(source.name) || textOf(source.companyId) || 'Empresa',
    route: companyRoute(source.companyId, source.shardCode),
    accountRoute: accountRoute(source.companyId, source.shardCode),
    soldText: d0 || EMPTY,
    soldSource: sold,
    approximate: sold.approximate,
    unsold: source.isSold === false,
    trialDays: numberOf(source.trialDays),
    trialText: isNumber(numberOf(source.trialDays)) ? (numberOf(source.trialDays) > 0 ? formatDays(numberOf(source.trialDays)) : 'Venda direta') : EMPTY,
    daysSinceSale: daysSinceSale,
    daysText: isNumber(daysSinceSale) ? dayText(daysSinceSale) : EMPTY,
    stage: stageInfo(source.stage) ? stageInfo(source.stage).stage : null,
    stageText: textOf(source.stageLabel) || stageLabel(source.stage),
    skippedSetup: source.skippedSetup === true,
    stuck: !!stuck,
    reasonKey: stuck ? textOf(stuck.reason) : '',
    reasonText: stuck ? reasonLabel(stuck.reason, stuck.label) : '',
    nextStep: stuck ? textOf(stuck.nextStep) : '',
    overdueText: isNumber(overdue) ? formatDays(overdue) : '',
    seatsText: isNumber(enabled) ? (isNumber(seats) && seats > 0 ? ratioText(enabled, seats) : formatInteger(enabled) + ' (sem assentos)') : EMPTY,
    seatsShareText: isNumber(seatsRatio) && isNumber(seats) && seats > 0 ? formatPercent(seatsRatio * 100) : '',
    registeredText: isNumber(registered) ? plural(registered, 'cadastrado', 'cadastrados') : '',
    employeesNowText: isNumber(enabled) ? formatInteger(enabled) + ' habilitados hoje' + (isNumber(registered) ? ' de ' + plural(registered, 'cadastrado', 'cadastrados') : '') : '',
    punchesText: firstPunch ? firstPunch + (lastPunch && lastPunch !== firstPunch ? ' → ' + lastPunch : '') : 'Sem batida',
    punch7dText: isNumber(days7) ? plural(days7, 'dia', 'dias') + (isNumber(users7) ? ' · ' + plural(users7, 'colab.', 'colab.') : '') : EMPTY,
    adminText: dateOrEmpty(source.adminLastActiveOn) || EMPTY,
    progressText: dateOrEmpty(source.lastProgressOn) || EMPTY,
    featuresText: isNumber(numberOf(source.featuresUsed30d)) ? plural(numberOf(source.featuresUsed30d), 'funcionalidade', 'funcionalidades') + ' em 30 dias' : '',
    profileText: profile.length ? profile.join(' · ') : EMPTY,
    originText: originLabel(source.origin),
    status: statusNowInfo(source.statusNow, source.isSold),
    windowText: windowRow ? windowValuesText(windowRow) : '',
    windowStageText: windowStage ? 'Etapa em ' + windowLabel(windowParam(windowRow.window)) + ': ' + (textOf(windowRow.stageLabel) || windowStage.label) : '',
    milestones: source.milestones,
  };
};

export const onboardingCompanyRows = (rows) => listOf(rows).map((row, index) => onboardingCompanyRow(row, index));

export const stuckCounters = (data) => listOf(objectOf(data).counters)
  .filter((item) => textOf(item.reason).trim())
  .map((item) => ({ key: textOf(item.reason).trim(), label: reasonLabel(item.reason, item.label), companies: numberOf(item.companies) }))
  .sort((a, b) => reasonOrder(a.key) - reasonOrder(b.key))
  .map((item) => Object.assign(item, { text: item.label + ' (' + (isNumber(item.companies) ? formatInteger(item.companies) : EMPTY) + ')' }));

export const drillTitle = (drill) => {
  const source = objectOf(drill);
  const window = windowParam(source.window);
  const until = window ? 'até ' + windowUntil(window) : '';
  if (source.kind === 'stage') return 'Pararam em "' + stageLabel(source.stage) + '" ' + until;
  const label = milestoneLabel(source.milestone);
  return (source.reached ? 'Chegaram a "' : 'Não chegaram a "') + label + '" ' + until;
};

export const drillToggleText = (drill) => (objectOf(drill).reached ? 'Ver as que não chegaram' : 'Ver as que chegaram');

const windowState = (found) => {
  if (!found) return { text: EMPTY, tone: 'muted', recorded: false };
  if (found.closed !== true) return { text: 'em andamento', tone: 'info', recorded: false };
  if (found.recorded !== true) return { text: 'aguardando medição', tone: 'warn', recorded: false };
  return { text: textOf(found.source).toUpperCase() === 'B' ? 'retroativa' : 'gravada no fechamento', tone: 'ok', recorded: true };
};

const countText = (value) => (isNumber(numberOf(value)) ? formatInteger(numberOf(value)) : EMPTY);

const weekText = (found) => {
  const days = numberOf(found.weekPunchDays);
  if (!isNumber(days)) return EMPTY;
  const users = numberOf(found.weekPunchUsers);
  const employees = numberOf(found.employees);
  if (!isNumber(users)) return plural(days, 'dia', 'dias');
  return plural(days, 'dia', 'dias') + ' · ' + (isNumber(employees) && employees > 0 ? ratioText(users, employees) + ' bateram' : plural(users, 'colab.', 'colab.'));
};

const companySituation = (company, statusNow) => {
  if (company.stuck) {
    return { label: 'Travada: ' + company.reasonText, tone: 'warn', detail: [company.nextStep, company.overdueText ? 'atrasada há ' + company.overdueText : ''].filter(Boolean).join(' · ') };
  }
  if (!company.companyId) return { label: EMPTY, tone: 'muted', detail: '' };
  if (company.unsold) return { label: 'Não vendida', tone: 'muted', detail: 'As travadas são acompanhadas só nas vendidas.' };
  if (textOf(statusNow) === 'churned') return { label: 'Fora do acompanhamento', tone: 'muted', detail: 'Cancelada ou excluída.' };
  if (isNumber(company.daysSinceSale) && company.daysSinceSale > 75) {
    return { label: 'Fora do acompanhamento', tone: 'muted', detail: 'As travadas são acompanhadas até D+75.' };
  }
  return { label: 'Em dia', tone: 'ok', detail: '' };
};

export const companyOnboardingView = (data) => {
  const source = objectOf(data);
  const company = onboardingCompanyRow(source.company, 0);
  const windows = ONBOARDING_WINDOWS.map((window) => {
    const found = byWindow(source.windows, window);
    const state = windowState(found);
    const values = state.recorded ? found : {};
    const stage = state.recorded ? stageInfo(found.stage) : null;
    return {
      key: String(window),
      window: window,
      label: windowLabel(window),
      closed: !!found && found.closed === true,
      recorded: state.recorded,
      sourceText: state.text,
      sourceTone: state.tone,
      employees: numberOf(values.employees),
      managers: numberOf(values.managers),
      punchDays: numberOf(values.punchDays),
      weekPunchDays: state.recorded ? numberOf(found.weekPunchDays) : null,
      weekPunchUsers: state.recorded ? numberOf(found.weekPunchUsers) : null,
      employeesText: countText(values.employees),
      managersText: countText(values.managers),
      punchDaysText: countText(values.punchDays),
      weekText: state.recorded ? weekText(found) : EMPTY,
      stageText: stage ? (textOf(found.stageLabel) || stage.label) : EMPTY,
    };
  });
  return {
    company: company,
    situation: companySituation(company, objectOf(source.company).statusNow),
    timeline: source.timeline !== undefined ? source.timeline : company.milestones,
    skippedSetup: company.skippedSetup,
    windows: windows,
    userActivation: metricOf(source.userActivation7d),
    empty: !company.companyId,
  };
};

const stuckOf = (row) => (row && isObject(row.stuck) ? row.stuck : null);

const stuckField = (row, field) => {
  const stuck = stuckOf(row);
  return stuck ? stuck[field] : null;
};

const reasonText = (row) => {
  const stuck = stuckOf(row);
  return stuck ? reasonLabel(stuck.reason, stuck.label) : '';
};

const listWindowOf = (row) => (row && isObject(row.window) ? row.window : null);

const recordedField = (row, field) => {
  const window = listWindowOf(row);
  return window && window.recorded === true ? window[field] : null;
};

const listWindowStage = (row) => {
  const window = listWindowOf(row);
  if (!window) return '';
  return recordedText(window) || textOf(window.stageLabel) || (stageInfo(window.stage) ? stageLabel(window.stage) : '');
};

const dateOrNull = (value) => (textOf(value).trim() ? textOf(value).trim() : null);

const statusNowText = (row) => {
  const label = statusNowInfo(row.statusNow, row.isSold).label;
  return label === EMPTY ? '' : label;
};

export const ONBOARDING_COMPANY_COLUMNS = defineColumns([
  { key: 'name', label: 'Empresa', type: 'text', infoKey: 'common.company', sortable: true, startDescending: false, value: (row) => textOf(row.name) || textOf(row.companyId) },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  {
    key: 'soldAt', label: 'Vendida em', type: 'date', infoKey: 'activation.soldAt', sortable: true, startDescending: true,
    value: (row) => dateOrNull(row.d0On) || dateOrNull(row.soldAt),
  },
  { key: 'soldSource', label: 'Fonte da venda', type: 'text', infoKey: 'activation.soldSource', screen: false, value: (row) => (textOf(row.soldSource).trim() ? soldSourceInfo(row.soldSource).label : '') },
  { key: 'trialDays', label: 'Dias de trial', type: 'int', infoKey: 'activation.trialDays', screen: false },
  { key: 'daysSinceSale', label: 'D+', type: 'int', infoKey: 'activation.daysSinceSale', sortable: true, startDescending: true },
  {
    key: 'stage', label: 'Etapa', type: 'text', infoKey: 'activation.stageNow', sortable: true, startDescending: false,
    value: (row) => textOf(row.stageLabel) || (stageInfo(row.stage) ? stageLabel(row.stage) : ''),
  },
  { key: 'skippedSetup', label: 'Pulou a configuração', type: 'text', infoKey: 'activation.skippedSetup', screen: false, value: (row) => (typeof row.skippedSetup === 'boolean' ? row.skippedSetup : null) },
  { key: 'reason', label: 'Motivo', type: 'text', infoKey: 'activation.stuckReason', value: reasonText },
  { key: 'overdueDays', label: 'Atrasada há', type: 'int', infoKey: 'activation.overdueDays', sortable: true, startDescending: true, value: (row) => stuckField(row, 'overdueDays') },
  { key: 'nextStep', label: 'Próximo passo', type: 'text', infoKey: 'activation.nextStep', value: (row) => textOf(stuckField(row, 'nextStep')) },
  {
    key: 'employees', label: 'Na janela', type: 'int', infoKey: 'activation.windowValues', sortable: true, startDescending: true, export: false,
    exportedAs: ['windowEmployees', 'windowPunchDays', 'windowPunchUsers', 'windowStage'],
  },
  { key: 'windowEmployees', label: 'Colaboradores na janela', type: 'int', infoKey: 'activation.windowValues', screen: false, value: (row) => recordedField(row, 'employees') },
  { key: 'windowPunchDays', label: 'Dias com batida na semana da janela', type: 'int', infoKey: 'activation.windowWeek', screen: false, value: (row) => recordedField(row, 'weekPunchDays') },
  { key: 'windowPunchUsers', label: 'Bateram na semana da janela', type: 'int', infoKey: 'activation.windowWeek', screen: false, value: (row) => recordedField(row, 'weekPunchUsers') },
  { key: 'windowStage', label: 'Etapa na janela', type: 'text', infoKey: 'activation.windowStage', screen: false, value: listWindowStage },
  {
    key: 'seats', label: 'Habilitados ÷ assentos', type: 'percent', infoKey: 'activation.seats', export: false,
    exportedAs: ['employeesRegistered', 'employeesNow', 'seatsContracted', 'seatsRatio'],
  },
  { key: 'employeesRegistered', label: 'Cadastrados', type: 'int', infoKey: 'activation.employeesRegistered', screen: false },
  { key: 'employeesNow', label: 'Habilitados hoje', type: 'int', infoKey: 'activation.employeesNow', screen: false },
  { key: 'seatsContracted', label: 'Assentos contratados', type: 'int', infoKey: 'common.seatsContracted', screen: false },
  {
    key: 'seatsRatio', label: 'Habilitados ÷ assentos', type: 'percent', infoKey: 'activation.seats', screen: false,
    value: (row) => (isNumber(numberOf(row.seatsRatio)) ? Math.round(numberOf(row.seatsRatio) * 10000) / 100 : null),
  },
  { key: 'punches', label: '1ª e última batida', type: 'date', infoKey: 'activation.punches', export: false, exportedAs: ['firstPunch', 'lastPunch'] },
  { key: 'firstPunch', label: '1ª batida', type: 'date', infoKey: 'activation.punches', screen: false, value: (row) => dateOrNull(row.firstPunchOn) },
  { key: 'lastPunch', label: 'Última batida', type: 'date', infoKey: 'activation.punches', screen: false, value: (row) => dateOrNull(row.lastPunchOn) },
  { key: 'punch7d', label: 'Batida 7d (dias · colab.)', type: 'int', infoKey: 'activation.punch7d', export: false, exportedAs: ['punchDays7d', 'punchUsers7d'] },
  { key: 'punchDays7d', label: 'Dias com batida 7d', type: 'int', infoKey: 'activation.punch7d', screen: false },
  { key: 'punchUsers7d', label: 'Colaboradores com batida 7d', type: 'int', infoKey: 'activation.punch7d', screen: false },
  { key: 'adminLastActive', label: 'Último acesso de admin', type: 'date', infoKey: 'activation.adminLastActive', value: (row) => dateOrNull(row.adminLastActiveOn) },
  {
    key: 'lastProgress', label: 'Último avanço', type: 'date', infoKey: 'activation.lastProgress', sortable: true, startDescending: true,
    value: (row) => dateOrNull(row.lastProgressOn),
  },
  { key: 'featuresUsed30d', label: 'Funcionalidades em 30 dias', type: 'int', infoKey: 'activation.featuresUsed30d', screen: false },
  { key: 'profile', label: 'Plano · Porte · Jornada', type: 'text', infoKey: 'activation.profile', export: false, exportedAs: ['plan', 'sizeBand', 'segment', 'journey'] },
  { key: 'plan', label: 'Plano (hoje)', type: 'text', infoKey: 'activation.profile', screen: false, value: (row) => planLabel(row.planCode) },
  { key: 'sizeBand', label: 'Porte', type: 'text', infoKey: 'common.sizeBand', screen: false, value: (row) => textOf(row.sizeBand).trim() },
  { key: 'segment', label: 'Perfil da empresa', type: 'text', infoKey: 'common.segment', screen: false, value: (row) => textOf(row.segment).trim() },
  { key: 'journey', label: 'Jornada', type: 'text', infoKey: 'common.journey', screen: false, value: (row) => journeyLabel(row.journey) },
  { key: 'origin', label: 'Origem', type: 'text', infoKey: 'activation.origin', screen: false, value: (row) => originLabel(row.origin) },
  { key: 'statusNow', label: 'Situação hoje', type: 'text', infoKey: 'activation.statusNow', screen: false, value: statusNowText },
]);

export const DRILL_ONLY_KEYS = ['employees', 'windowEmployees', 'windowPunchDays', 'windowPunchUsers', 'windowStage'];

export const companyColumns = (mode) => {
  const sortable = SORTABLE[mode] || [];
  return ONBOARDING_COMPANY_COLUMNS
    .filter((column) => mode === 'companies' || DRILL_ONLY_KEYS.indexOf(column.key) === -1)
    .map((column) => Object.assign({}, column, { sortable: column.sortable && sortable.indexOf(column.key) !== -1 }));
};

const cellIn = (cells, window) => listOf(cells).filter((cell) => cell && cell.window === window)[0] || {};

const cellField = (list, field, window) => (row) => {
  const value = cellIn(row ? row[list] : null, window)[field];
  return value === undefined ? null : value;
};

const matrixWindowColumns = (window) => {
  const label = windowLabel(window);
  return [
    { key: 'd' + window, label: label, type: 'metric', unit: 'pct', infoKey: 'activation.windowRate', value: cellField('cells', 'rate', window) },
    { key: 'd' + window + 'Reached', label: label + ' — atingiram', type: 'int', infoKey: 'activation.reached', screen: false, value: cellField('cells', 'reached', window) },
    { key: 'd' + window + 'Base', label: label + ' — base', type: 'int', infoKey: 'activation.base', screen: false, value: cellField('cells', 'base', window) },
    {
      key: 'd' + window + 'NotMeasured', label: label + ' — não medidas', type: 'int', infoKey: 'activation.notMeasured', screen: false,
      value: cellField('cells', 'notMeasured', window),
    },
    { key: 'd' + window + 'Median', label: label + ' — mediana', type: 'decimal', infoKey: 'activation.quantity', screen: false, value: cellField('cells', 'median', window) },
    { key: 'd' + window + 'P75', label: label + ' — p75', type: 'decimal', infoKey: 'activation.quantity', screen: false, value: cellField('cells', 'p75', window) },
  ];
};

export const ONBOARDING_MATRIX_COLUMNS = defineColumns(ONBOARDING_WINDOWS.reduce((all, window) => all.concat(matrixWindowColumns(window)), [
  { key: 'group', label: 'Grupo', type: 'text', infoKey: 'activation.milestoneGroup' },
  { key: 'milestone', label: 'Marco', type: 'text', infoKey: 'activation.milestone', value: (row) => row.label },
]).concat([
  { key: 'bottleneck', label: 'Gargalo', type: 'text', infoKey: 'activation.bottleneck', screen: false },
]));

export const matrixTableRows = (data) => {
  const rows = [];
  matrixGroups(data, false).forEach((group) => {
    group.rows.forEach((row) => rows.push({ key: row.key, group: group.label, label: row.label, bottleneck: !!row.bottleneck, cells: row.cells }));
  });
  quantityRows(data).forEach((row) => rows.push({ key: row.key, group: QUANTITY_GROUP, label: row.label, bottleneck: null, cells: row.cells }));
  return rows;
};

export const ONBOARDING_STAGE_COLUMNS = defineColumns([
  { key: 'window', label: 'Janela', type: 'text', infoKey: 'activation.window' },
  { key: 'stage', label: 'Etapa', type: 'text', infoKey: 'activation.stageWindow' },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'activation.stageCompanies' },
  { key: 'share', label: 'Participação', type: 'percent', infoKey: 'activation.stageCompanies' },
  { key: 'base', label: 'Empresas com a janela fechada', type: 'int', infoKey: 'activation.base' },
  { key: 'notMeasured', label: 'Não medidas', type: 'int', infoKey: 'activation.notMeasured' },
]);

export const stageTableRows = (data) => {
  const rows = [];
  stageBarRows(data).forEach((bar) => {
    bar.segments.forEach((segment) => rows.push({
      key: segment.key,
      window: bar.label,
      stage: segment.stage + ' · ' + segment.label,
      companies: segment.companies,
      share: segment.share,
      base: bar.eligible,
      notMeasured: bar.notMeasured,
    }));
  });
  return rows;
};

export const ONBOARDING_BOTTLENECK_COLUMNS = defineColumns([
  { key: 'window', label: 'Janela', type: 'text', infoKey: 'activation.window', value: (row) => row.windowText },
  { key: 'from', label: 'Da etapa', type: 'text', infoKey: 'activation.conversion', value: (row) => row.fromText },
  { key: 'to', label: 'Para a etapa', type: 'text', infoKey: 'activation.conversion', value: (row) => row.toText },
  { key: 'conversion', label: 'Conversão', type: 'metric', unit: 'pct', infoKey: 'activation.conversion', value: (row) => (row.conversion ? row.conversion.metric : null) },
  { key: 'lost', label: 'Não avançaram', type: 'int', infoKey: 'activation.conversionBase' },
  { key: 'base', label: 'Base', type: 'int', infoKey: 'activation.conversionBase' },
  { key: 'text', label: 'Leitura', type: 'text', infoKey: 'activation.bottleneck' },
]);

export const ONBOARDING_TIME_COLUMNS = defineColumns([
  { key: 'milestone', label: 'Marco', type: 'text', infoKey: 'activation.milestone', value: (row) => row.label },
  { key: 'reached', label: 'Atingiram', type: 'int', infoKey: 'activation.timeReached' },
  { key: 'eligible', label: 'Empresas com D60 fechado', type: 'int', infoKey: 'activation.timeReached', screen: false },
  { key: 'median', label: 'Mediana', type: 'decimal', infoKey: 'activation.timeMedian', value: (row) => row.medianDays },
  { key: 'p75', label: 'p75', type: 'decimal', infoKey: 'activation.timeP75', value: (row) => row.p75Days },
]);

export const ONBOARDING_TTV_COLUMNS = defineColumns([
  { key: 'band', label: 'Faixa', type: 'text', infoKey: 'activation.timeToValue', value: (row) => row.label },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'activation.timeToValue', value: (row) => row.value },
  { key: 'share', label: 'Participação', type: 'percent', infoKey: 'activation.timeToValue' },
]);

const cohortWindowColumns = (window) => {
  const label = windowLabel(window);
  return [
    { key: 'd' + window, label: label, type: 'metric', unit: 'pct', infoKey: 'activation.cohortWindow', value: cellField('windows', 'rate', window) },
    { key: 'd' + window + 'Base', label: label + ' — base', type: 'int', infoKey: 'activation.base', screen: false, value: cellField('windows', 'eligible', window) },
    { key: 'd' + window + 'Open', label: label + ' — em andamento', type: 'int', infoKey: 'activation.windowPending', screen: false, value: cellField('windows', 'open', window) },
    {
      key: 'd' + window + 'Awaiting', label: label + ' — aguardando medição', type: 'int', infoKey: 'activation.windowPending', screen: false,
      value: cellField('windows', 'awaiting', window),
    },
  ];
};

export const ONBOARDING_COHORT_COLUMNS = defineColumns(ONBOARDING_WINDOWS.reduce((all, window) => all.concat(cohortWindowColumns(window)), [
  { key: 'cohort', label: 'Coorte', type: 'text', infoKey: 'activation.cohort', value: (row) => row.label },
  { key: 'sold', label: 'Vendidas', type: 'int', infoKey: 'activation.cohortSize', value: (row) => row.count },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'activation.cohortSize', value: (row) => row.count },
  { key: 'soldCount', label: 'Vendidas', type: 'int', infoKey: 'activation.cohortSize', screen: false, value: (row) => row.sold },
  { key: 'purged', label: 'Expurgadas', type: 'int', infoKey: 'activation.purged', screen: false },
  { key: 'smallSample', label: 'Amostra pequena', type: 'text', infoKey: 'activation.smallSample', screen: false },
]).concat([
  { key: 'sourceB', label: 'Retroativa', type: 'percent', infoKey: 'activation.cohortRetroactive', value: (row) => row.sourceBShare },
  { key: 'medianPunch', label: 'Mediana até a 1ª batida', type: 'metric', unit: 'days', infoKey: 'activation.cohortMedianPunch', value: (row) => row.medianDaysToPunch },
  { key: 'medianEmployees', label: 'Colaboradores em D30 (mediana)', type: 'decimal', infoKey: 'activation.cohortMedianEmployees', value: (row) => row.medianEmployees30 },
]));

const SOLD_UNIVERSE_HIDDEN = ['companies', 'soldCount'];

const OTHER_UNIVERSE_HIDDEN = ['sold'];

export const cohortColumns = (universe) => {
  const hidden = universe ? OTHER_UNIVERSE_HIDDEN : SOLD_UNIVERSE_HIDDEN;
  return ONBOARDING_COHORT_COLUMNS.filter((column) => hidden.indexOf(column.key) === -1);
};

export const cohortTableRows = (rows, indicator, granularity) => {
  const wanted = validIndicator(indicator);
  return sortedCohorts(rows).reverse().map((row) => {
    const companies = numberOf(row.companies);
    return {
      key: cohortKey(row.cohort),
      label: cohortLabel(row.cohort, granularity),
      count: isNumber(companies) ? companies : numberOf(row.sold),
      sold: numberOf(row.sold),
      purged: numberOf(row.purged),
      smallSample: smallSampleOf(row),
      sourceBShare: numberOf(row.sourceBShare),
      medianDaysToPunch: metricOf(row.medianDaysToPunch),
      medianEmployees30: numberOf(row.medianEmployees30),
      windows: ONBOARDING_WINDOWS.map((window) => {
        const found = byWindow(row.windows, window);
        return {
          window: window,
          rate: found ? indicatorMetric(found, wanted) : null,
          eligible: found ? numberOf(found.eligible) : null,
          open: found ? numberOf(found.open) : null,
          awaiting: found ? numberOf(found.awaiting) : null,
        };
      }),
    };
  });
};

export const ONBOARDING_WINDOW_COLUMNS = defineColumns([
  { key: 'window', label: 'Janela', type: 'text', infoKey: 'activation.window', value: (row) => row.label },
  { key: 'recording', label: 'Gravação', type: 'text', infoKey: 'activation.windowRecording', value: (row) => row.sourceText },
  { key: 'employees', label: 'Colaboradores', type: 'int', infoKey: 'activation.windowValues' },
  { key: 'managers', label: 'Gestores ou admins', type: 'int', infoKey: 'activation.windowManagers' },
  { key: 'punchDays', label: 'Dias com batida', type: 'int', infoKey: 'activation.windowPunchDays' },
  { key: 'week', label: 'Semana da janela', type: 'text', numeric: true, infoKey: 'activation.windowWeek', value: (row) => (row.recorded ? row.weekText : '') },
  { key: 'stage', label: 'Etapa', type: 'text', infoKey: 'activation.windowStage', value: (row) => (row.recorded ? row.stageText : '') },
]);



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piOnboardingModel.js